import { HttpErrorResponse } from "@angular/common/http";
import {
  inject,
  Injectable,
  Injector,
  runInInjectionContext
} from "@angular/core";
import { attemptAsync } from "es-toolkit";
import { lastValueFrom } from "rxjs";
import { sntLog } from "src/app/common/logger";
import {
  BrokerEventLoaderFn,
  BrokerEventResolverConfig
} from "src/app/sse/broker-event.types";
import { BROKER_EVENT_LOADERS } from "src/app/sse/broker-loaders.registry";
import { SseEvents } from "src/app/sse/sse-events";

const logger = sntLog("BrokerLoadersService");

@Injectable({
  providedIn: "root"
})
export class BrokerResolverService {
  readonly #loaderMap = new Map<
    SseEvents,
    {
      resolver: BrokerEventLoaderFn | undefined;
      resolverLight: BrokerEventLoaderFn | undefined;
    }
  >();
  readonly #injector = inject(Injector);

  registerLoaders(): void {
    BROKER_EVENT_LOADERS.forEach(
      ([events, config]: [SseEvents[], BrokerEventResolverConfig]) => {
        const resolver: BrokerEventLoaderFn | undefined = config.resolver;
        const resolverLight: BrokerEventLoaderFn | undefined =
          config.resolverLight;

        if (!resolver && !resolverLight) {
          return;
        }

        events.forEach((event: SseEvents) => {
          this.#loaderMap.set(event, {
            resolver,
            resolverLight
          });
        });
      }
    );
  }

  async loadFnForEvent({
    event,
    resource
  }: {
    event: SseEvents;
    resource: string;
  }): Promise<unknown | undefined> {
    const resolverConfig = this.#loaderMap.get(event);
    if (!resolverConfig) {
      logger.warn(`loadFnForEvent: no loader found for event "${event}"`);
      return undefined;
    }

    if (!resource) {
      logger.warn(`loadFnForEvent: no resource found for event "${event}"`);
      return undefined;
    }

    const [error, result] = await attemptAsync(async () => {
      const { resolver, resolverLight } = resolverConfig;

      let result: unknown | undefined;
      let resultFound: boolean = false;

      if (resolver) {
        const [errorResolver, resultResolver] = await attemptAsync(async () => {
          return await runInInjectionContext(this.#injector, () =>
            lastValueFrom(resolver({ resource }))
          );
        });

        if (errorResolver) {
          if (isError403(errorResolver)) {
            result = undefined;
          } else {
            throw errorResolver;
          }
        }

        if (!errorResolver) {
          resultFound = true;
          result = resultResolver;
        }
      }

      if (resultFound) {
        return result;
      }

      if (!resolverLight) {
        return result;
      }

      const [errorResolverLight, resultResolverLight] = await attemptAsync(
        async () => {
          return await runInInjectionContext(this.#injector, () =>
            lastValueFrom(resolverLight({ resource }))
          );
        }
      );

      if (errorResolverLight) {
        throw errorResolverLight;
      }

      return resultResolverLight;
    });

    if (error) {
      throw error;
    }

    return result;
  }
}

function isError403(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 403;
}
